import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

const base = 'inline-flex h-8 items-center rounded-control px-3.5 text-caption transition-colors';

/** MarketNest filter chip — 32px, 4px radius, terracotta when selected. */
const filterChip = cva(`${base} border`, {
  variants: {
    selected: {
      true: 'border-brand bg-brand text-brand-ink',
      false: 'border-border-strong bg-transparent text-ink-muted hover:bg-brand-tint',
    },
  },
  defaultVariants: { selected: false },
});

export type FilterChipProps = ComponentProps<'button'> & VariantProps<typeof filterChip>;

export function FilterChip({ className, selected, ...props }: FilterChipProps) {
  return (
    <button
      type="button"
      aria-pressed={selected ?? false}
      className={cn(filterChip({ selected }), className)}
      {...props}
    />
  );
}

/** MarketNest status chip — a tinted pair per state, no border. */
const statusChip = cva(base, {
  variants: {
    tone: {
      success: 'bg-success-tint text-success-ink',
      warning: 'bg-warning-tint text-warning-ink',
      error: 'bg-danger-tint text-danger-ink',
    },
  },
  defaultVariants: { tone: 'success' },
});

export type StatusChipProps = ComponentProps<'span'> & VariantProps<typeof statusChip>;

export function StatusChip({ className, tone, ...props }: StatusChipProps) {
  return <span className={cn(statusChip({ tone }), className)} {...props} />;
}
